import { useMutation, useQuery } from "@tanstack/react-query";
import { MappingSourceType } from "../config/enums/MappingSourceType.enum";
import {
  ApiServiceErr,
  MutOptions,
  axiosApi,
  calculatorApi,
} from "./apiService";

interface ImpactCategoryOptions {
  categoryName?: string;
  salesUnitId?: string;
  productId?: string;
}

interface ProductPayloadOptions {
  salesUnitId?: string;
  productId?: string;
}

interface SimulationDrawing {
  drawingId?: string;
}

interface GetProductSimulationsOptions {
  pageNumber: number;
  pageSize: number;
  productId?: string;
  salesUnitId?: string;
}

export const useGetImpactCategory = ({
  categoryName,
  salesUnitId,
  productId,
}: ImpactCategoryOptions) =>
  useQuery<ImpactCategory[], ApiServiceErr>(
    [categoryName, salesUnitId, productId, "useGetImpactCategory"],
    async () => {
      const response = await axiosApi.get(
        `/drawing-tool/${MappingSourceType.product}/impact-category/${categoryName}`,
        {
          params: { salesUnitId, productId },
        }
      );
      return response.data;
    },
    {
      enabled: !!categoryName && !!salesUnitId && !!productId,
    }
  );

export const useGetProductPayload = ({
  salesUnitId,
  productId,
}: ProductPayloadOptions) =>
  useQuery<ImpactCategory, ApiServiceErr>(
    [salesUnitId, productId, "useGetProductPayload"],
    async () => {
      if (!salesUnitId) {
        return null;
      }
      const response = await axiosApi.get(
        `/drawing-tool/${MappingSourceType.product}/queue-data/${salesUnitId}/${productId}`
      );
      return response.data;
    },
    {
      enabled: !!salesUnitId,
    }
  );

export const useSimulate = (opt?: MutOptions<string>) =>
  useMutation<any, ApiServiceErr, any>(async (data) => {
    const response = await calculatorApi.post("/simulation", data);
    return response.data;
  }, opt);

export const useCreateProductSimulation = (opt?: MutOptions<string>) =>
  useMutation<any, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.post("/product-simulation", data);
    return response.data;
  }, opt);

export const useUpdateProductSimulation = (opt?: MutOptions<string>) =>
  useMutation<any, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.put(
      `/product-simulation/${data._id}`,
      data
    );
    return response.data;
  }, opt);

export const useGetProductSimulations = (
  params: GetProductSimulationsOptions
) => {
  return useQuery<any, ApiServiceErr>(
    ["product-simulation", params],
    async () => {
      const response = await axiosApi.get("/product-simulation", { params });
      return response.data;
    }
  );
};

export const useDeleteProductSimulation = (opt?: MutOptions<string>) =>
  useMutation<any, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.delete(`/product-simulation/${data._id}`);
    return response.data;
  }, opt);

export const useGetSimulationDrawingData = ({ drawingId }: SimulationDrawing) =>
  useQuery<any, ApiServiceErr>(
    [drawingId, "useGetSimulationDrawing"],
    async () => {
      if (!drawingId) {
        return null;
      }
      const response = await axiosApi.get(
        `/drawing-tool/${MappingSourceType.simulation}/summaries/${drawingId}`
      );
      return response.data;
    },
    {
      enabled: !!drawingId,
    }
  );
